import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { SettingsShell, useSettings, SaveBar } from "./SettingsShell";
import api, { fileUrl, formatErr } from "../../lib/apiClient";

const INDUSTRIES = [
  "Plumbing", "HVAC", "Electrical", "Roofing", "Landscaping", "Cleaning",
  "General Contracting", "Pest Control", "Painting", "Handyman", "Other",
];

const TIME_ZONES = [
  "America/Toronto (Eastern)",
  "America/Vancouver (Pacific)",
  "America/Edmonton (Mountain)",
  "America/Winnipeg (Central)",
  "America/Halifax (Atlantic)",
  "America/St_Johns (Newfoundland)",
];

/** Company Info → Profile sub-tab only (Business hours / Service area intentionally not built). */
export default function SettingsCompany() {
  const { settings, patch, save, loading, saving, saved, err } = useSettings();
  const [logoVersion, setLogoVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef();

  if (loading || !settings) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ logo_path: (settings.logo_path || "uploaded") + "" });
      setLogoVersion((v) => v + 1);
    } catch (e2) { setUploadErr(formatErr(e2)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <SettingsShell
      title="Company"
      subtitle="Company profile shown on invoices, estimates, and customer-facing pages."
      testid="settings-company"
    >
      <div className="space-y-8">
        <Section title="Business information" testid="section-business-info">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business name" testid="company-business-name" value={settings.business_name || ""} onChange={(v) => patch({ business_name: v })} />
            <Field label="Business phone" testid="company-phone" value={settings.phone || ""} onChange={(v) => patch({ phone: v })} />
            <Field label="Support email" testid="company-email" type="email" value={settings.email || ""} onChange={(v) => patch({ email: v })} />
            <Field label="Website" testid="company-website" placeholder="https://" value={settings.website || ""} onChange={(v) => patch({ website: v })} />
            <Field label="License number" testid="company-license" value={settings.license_number || ""} onChange={(v) => patch({ license_number: v })} />
            <Field label="Legal entity name" testid="company-legal-entity" value={settings.legal_entity_name || ""} onChange={(v) => patch({ legal_entity_name: v })} />
            <div>
              <Label>Industry</Label>
              <select
                data-testid="company-industry"
                value={settings.industry || ""}
                onChange={(e) => patch({ industry: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              >
                <option value="">Select industry…</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <Label>Time Zone</Label>
              <select
                data-testid="company-timezone"
                value={settings.time_zone || ""}
                onChange={(e) => patch({ time_zone: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              >
                <option value="">Select time zone…</option>
                {TIME_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label>Address</Label>
              <textarea
                data-testid="company-address"
                rows={3}
                value={settings.address || ""}
                onChange={(e) => patch({ address: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
          </div>
        </Section>

        <Section title="Canadian Tax ID" testid="section-tax-id">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="HST / GST number" testid="company-hst-number" placeholder="e.g. 741398747RT0001" value={settings.hst_number || ""} onChange={(v) => patch({ hst_number: v })} />
          </div>
        </Section>

        <Section title="Company description" testid="section-description">
          <textarea
            data-testid="company-description"
            rows={4}
            value={settings.company_description || ""}
            onChange={(e) => patch({ company_description: e.target.value })}
            placeholder="Short tagline or description that appears on customer-facing pages."
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
          />
        </Section>

        <Section title="Logo" testid="section-logo">
          <div className="flex items-center gap-5">
            {settings.logo_path && (
              <img
                key={logoVersion}
                src={fileUrl(`/settings/logo`) + `&v=${logoVersion}`}
                alt="Logo"
                className="h-20 w-auto max-w-[160px] object-contain border border-slate-200 rounded p-2"
                data-testid="company-logo-preview"
              />
            )}
            <label data-testid="company-logo-upload" className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 cursor-pointer flex items-center gap-2">
              <Upload size={14} /> {uploading ? "Uploading…" : settings.logo_path ? "Replace logo" : "Upload logo"}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
            </label>
            <span className="text-xs text-slate-500">PNG or JPG · max 5MB</span>
          </div>
          {uploadErr && <p className="text-sm text-red-600 mt-2">{uploadErr}</p>}
        </Section>

        <Section title="Message on invoice, receipt, and estimate" testid="section-message">
          <textarea
            data-testid="company-invoice-message"
            rows={4}
            value={settings.invoice_footer_message || ""}
            onChange={(e) => patch({ invoice_footer_message: e.target.value })}
            placeholder="Thank you for your business. Payments due upon receipt."
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
          />
          <p className="text-[11px] text-slate-500 mt-1">Appears at the bottom of every invoice, receipt, and estimate.</p>
        </Section>

        <Section title="Terms and conditions" testid="section-terms">
          <textarea
            data-testid="company-terms"
            rows={8}
            value={settings.invoice_terms_long || ""}
            onChange={(e) => patch({ invoice_terms_long: e.target.value })}
            placeholder="Paste your full Terms & Conditions here…"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] font-mono"
          />
          <p className="text-[11px] text-slate-500 mt-1">Published at <code className="text-[#0F4C81]">/api/public/terms</code> and linked from every invoice email.</p>
        </Section>
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} err={err} testid="settings-company" />
    </SettingsShell>
  );
}

/* ------------ helpers ------------ */

function Section({ title, testid, children }) {
  return (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-6">
      <h2 className="font-display font-semibold text-lg text-slate-900 mb-4 pb-2 border-b border-slate-100">{title}</h2>
      {children}
    </section>
  );
}

function Label({ children }) {
  return <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">{children}</label>;
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
      />
    </div>
  );
}
